/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import { t } from '@sportninja/common/i18n'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import cn from 'classnames'
import * as csv from 'csvtojson'
import dayjs from 'dayjs'
import React from 'react'
import SuspensionSelect from '../../components/Suspensions/SuspensionSelect'
import DatePicker from '../DatePicker'
import { FormButton } from '../Form/css'
import Icon from '../Icon'
import ReportsTableComponent from './ReportsTableComponent'

const dropDownOptions = [
  {
    text: 'Team Penalty Minutes',
    value: 'penaltiesByTeam',
  },
  {
    text: 'Team Registrations',
    value: 'registrations',
  },
  {
    text: 'Games Played',
    value: 'gamesPlayed',
  },
]

const reportEndpoints = {
  penaltiesByTeam: '/reports/:entityId/team_penalty_minutes',
  registrations: '/reports/:entityId/registration',
  gamesPlayed: '/reports/:entityId/games_played',
}

const reportOrgEndpoints = {
  penaltiesByTeam: '/reports/org/:entityId/team_penalty_minutes',
  registrations: '/reports/org/:entityId/registration',
  gamesPlayed: '/reports/org/:entityId/games_played',
}

const generateHref = (string, reportName, ref) => {
  var blob = new Blob([string], { type: 'text/csv;charset=utf-8;' })

  if (navigator.msSaveBlob) {
    // IE 10+
    navigator.msSaveBlob(blob, reportName)
  } else {
    const link = ref.current
    if (link.download !== undefined) {
      var url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', reportName)
    }
  }
}

const NewReports = ({ entityId, entityType }) => {
  const [schedules, setSchedules] = React.useState([])
  const [schedule, setSchedule] = React.useState('')
  const [report, setReport] = React.useState('penaltiesByTeam')
  const [loading, setLoading] = React.useState(false)
  const [csvString, setCSVString] = React.useState(false)
  const [data, setData] = React.useState(false)
  const [dateRange, setDateRange] = React.useState({
    starts_at: undefined,
    ends_at: undefined,
  })
  const downloadRef = React.useRef(null)

  React.useEffect(() => {
    const getSchedules = async () => {
      try {
        setLoading(true)
        const { data } = await req(
          `/organizations/${entityId}/schedules?sort=starts_at&direction=desc`
        )
        setSchedules(
          data.map((schedule) => ({ text: schedule.name, value: schedule.id }))
        )
      } catch (e) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    }
    if (entityType === ENTITY_TYPES.org) {
      getSchedules()
    }
  }, [entityId, entityType])

  const onView = async () => {
    try {
      setLoading(true)
      const query = {}
      if (dateRange.starts_at && dateRange.ends_at) {
        query.starts_after = dayjs(dateRange.starts_at)
          .hour(0)
          .minute(0)
          .second(0)
          .format()
        query.starts_before = dayjs(dateRange.ends_at)
          .hour(0)
          .minute(0)
          .second(0)
          .add(1, 'day')
          .format()
      }
      query.format = 'csv'

      let url = ''
      if (entityType === ENTITY_TYPES.org) {
        if (schedule === '') {
          url = reportOrgEndpoints[report].replace(':entityId', entityId)
        } else {
          url = reportEndpoints[report].replace(':entityId', schedule)
        }
      } else {
        url = reportEndpoints[report].replace(':entityId', entityId)
      }
      const response = await req(url, { parseJSON: false, query })
      const text = await response.text()
      setCSVString(text)
      generateHref(text, report, downloadRef)
      csv({ output: 'json' })
        .fromString(text)
        .then((result) => {
          setData(result)
        })
    } catch (e) {
      console.error(e)
      alert(
        `There was an error generating the report. ${
          e?.message || ''
        } Please try again.`
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      css={css`
        margin: 24px auto;
        display: flex;
        flex-direction: column;
        flex-wrap: wrap;
        align-items: flex-start;
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: row;
          justify-content: center;
          align-items: center;
        `}
      >
        <SuspensionSelect
          className={cn('game-list-filters-team', {
            'is-selected': !!report,
          })}
          disabled={loading}
          enabledAllItemsOption={false}
          label={t('util:capitalize', { text: 'Report Type' })}
          value={report}
          onChange={(selectedReport) => {
            setReport(selectedReport)
          }}
          options={dropDownOptions}
        />
        {entityType === ENTITY_TYPES.org ? (
          <SuspensionSelect
            className={cn('game-list-filters-team', {
              'is-selected': !!schedule,
            })}
            disabled={loading}
            enabledAllItemsOption={true}
            label={t('util:capitalize', {
              text: isCanlan ? 'Season' : 'Competition',
            })}
            value={schedule}
            onChange={(selectedReport) => {
              setSchedule(selectedReport)
            }}
            options={schedules}
          />
        ) : null}
        <div
          css={css`
            margin: 0 16px 16px 0;
            width: 260px;
            min-width: 260px;

            .picker-label {
              height: 12px;
              font-size: 12px;
              line-height: 12px;
              margin-bottom: 8px;
              letter-spacing: 1.04px;
            }

            .picker-value-container {
              background: #27303e;
              border: ${isCanlan ? 2 : 1}px solid #3f4753;
              border-radius: 4px;
              height: 40px;
            }
          `}
        >
          <DatePicker
            datePlaceholder='Select a date range'
            defaultValues={(() => {
              const values = {}
              if (dateRange.starts_at)
                values.starts_at = { value: dateRange.starts_at }
              if (dateRange.ends_at)
                values.ends_at = { value: dateRange.ends_at }
              return values
            })()}
            disabled={loading}
            hideTime
            label='Date Range'
            onChange={({ target }) => {
              try {
                const { name, value } = target
                setDateRange((prevDateRange) => {
                  return {
                    ...prevDateRange,
                    [name]: value,
                  }
                })
              } catch (e) {
                console.error(e)
              }
            }}
            useRange
          />
        </div>
        <FormButton
          isSubmit
          css={css`
            height: 46px;
            width: 100px;
          `}
          onClick={onView}
        >
          <Icon
            name='search'
            css={css`
              margin-right: 6px;
              transform: translateY(-1px);
            `}
            fontSize={14}
          />
          View
        </FormButton>
        <FormButton
          as='a'
          disabled={!csvString}
          isSubmit
          css={css`
            width: 200px;
            height: 46px;
            text-align: center;
            padding-top: 12px;
            margin-left: 16px;
          `}
          ref={downloadRef}
        >
          <Icon
            name='download'
            css={css`
              margin-right: 6px;
            `}
            fontSize={14}
          />
          Download CSV
        </FormButton>
      </div>
      {data && data?.length === 0 ? (
        <div
          css={css`
            margin: 24px auto;
            font-size: 16px;
            line-height: 24px;
            text-align: center;
            color: #ffffff;
          `}
        >
          No data found for the selected filters.
        </div>
      ) : null}
      {data?.length > 0 ? (
        <ReportsTableComponent>
          <thead>
            <tr>
              {Array.isArray(data) &&
                Object.keys(data?.[0])?.map((keyName) => {
                  return <th key={keyName}>{keyName}</th>
                })}
            </tr>
          </thead>
          <tbody>
            {Array.isArray(data) &&
              data?.map((row) => {
                return (
                  <tr key={row?.id}>
                    {Object.keys(row)?.map((keyName) => {
                      return (
                        <td key={keyName}>
                          {row[keyName] !== '' ? row[keyName] : '-'}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
          </tbody>
        </ReportsTableComponent>
      ) : null}
    </div>
  )
}

export default NewReports
